<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  modelValue: { type: Array, required: true },
  options: { type: Array, required: true },
  title: { type: String, required: true },
});
const emit = defineEmits(['update:modelValue']);
const { t } = useI18n();
const search = ref('');
const label = key => t(`KANBAN_VIEW.CRM.FILTERS.${key}`);
const normalize = value =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
const visibleOptions = computed(() =>
  props.options.filter(option =>
    normalize(option.name).includes(normalize(search.value.trim()))
  )
);
const selection = computed(() =>
  props.options
    .filter(option => props.modelValue.includes(option.id))
    .map(option => option.name)
    .join(', ')
);
const toggle = (id, checked) =>
  emit(
    'update:modelValue',
    checked
      ? [...props.modelValue, id]
      : props.modelValue.filter(value => value !== id)
  );
</script>

<template>
  <details
    name="crm-pipeline-menu"
    class="relative"
    @keydown.esc="$event.currentTarget.removeAttribute('open')"
  >
    <summary
      class="flex max-w-64 cursor-pointer list-none items-center gap-2 rounded-lg border border-n-weak px-3 py-2 text-xs text-n-slate-12"
    >
      <span>{{ title }}</span>
      <span
        v-if="modelValue.length"
        class="rounded bg-n-alpha-2 px-1.5"
        :title="selection"
        >{{ modelValue.length }}</span
      >
      <span class="i-lucide-chevron-down" />
    </summary>
    <div
      class="absolute left-0 top-10 z-50 w-72 max-w-[85vw] rounded-xl border border-n-weak bg-n-solid-2 p-3 shadow-xl"
    >
      <input
        v-model="search"
        type="search"
        :placeholder="label('SEARCH_OPTIONS')"
        :aria-label="label('SEARCH_OPTIONS')"
        class="mb-2 h-9 w-full rounded-lg border border-n-weak bg-n-solid-1 px-2 text-sm"
      />
      <label
        class="flex cursor-pointer items-center gap-3 rounded-lg px-2 py-2 text-sm text-n-slate-12 hover:bg-n-alpha-1"
      >
        <input
          type="checkbox"
          :checked="!modelValue.length"
          class="h-4 w-4 shrink-0"
          @change="emit('update:modelValue', [])"
        />
        <span>{{ label('ALL_OPTIONS') }}</span>
      </label>
      <div class="max-h-56 overflow-y-auto border-t border-n-weak pt-1">
        <label
          v-for="option in visibleOptions"
          :key="option.id"
          class="flex cursor-pointer items-start gap-3 rounded-lg px-2 py-2 text-sm text-n-slate-12 hover:bg-n-alpha-1"
        >
          <input
            type="checkbox"
            :checked="modelValue.includes(option.id)"
            :value="option.id"
            class="mt-0.5 h-4 w-4 shrink-0"
            @change="toggle(option.id, $event.target.checked)"
          />
          <span class="min-w-0 break-words">{{ option.name }}</span>
        </label>
        <p
          v-if="!visibleOptions.length"
          class="px-2 py-3 text-xs text-n-slate-10"
        >
          {{ label('NO_OPTIONS') }}
        </p>
      </div>
    </div>
  </details>
</template>
